import { normalizeId } from './ids';

const hasPositiveBalls = (value) => Number(value) > 0;

const parseOversToBalls = (value) => {
  if (typeof value === 'number') {
    return value > 0 ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const normalized = value.trim();
  if (!normalized) {
    return 0;
  }

  const wholeOversMatch = normalized.match(/^(\d+)$/);
  if (wholeOversMatch) {
    return Number(wholeOversMatch[1]) * 6;
  }

  const oversMatch = normalized.match(/^(\d+)\.(\d+)$/);
  if (!oversMatch) {
    return 0;
  }

  const overs = Number(oversMatch[1]);
  const balls = Number(oversMatch[2]);
  if (!Number.isFinite(overs) || !Number.isFinite(balls)) {
    return 0;
  }

  return overs * 6 + balls;
};

const hasDeliveryEvents = (events) => {
  if (!Array.isArray(events) || events.length === 0) {
    return false;
  }

  return events.some((event) => {
    if (!event || typeof event !== 'object') return false;
    if (Number(event?.ballNumber) > 0 || Number(event?.ball) > 0 || Number(event?.deliveryNumber) > 0) {
      return true;
    }

    const type = `${event?.type || event?.eventType || ''}`.toLowerCase();
    return type.includes('ball') || type.includes('delivery');
  });
};

export function hasFirstBallBowled(match) {
  if (!match || typeof match !== 'object') {
    return false;
  }

  if (match?.score?.firstBallBowledAt) {
    return true;
  }

  if (hasPositiveBalls(match?.score?.ballsBowled)) {
    return true;
  }

  if (hasPositiveBalls(match?.currentInnings?.ballsBowled)) {
    return true;
  }

  if (hasPositiveBalls(match?.score?.balls) || hasPositiveBalls(match?.summary?.balls)) {
    return true;
  }

  if (hasPositiveBalls(match?.currentInnings?.balls) || hasPositiveBalls(match?.currentInnings?.totalBalls)) {
    return true;
  }

  if (Array.isArray(match?.score?.innings)) {
    const inningsHasBalls = match.score.innings.some((innings) =>
      hasPositiveBalls(innings?.ballsBowled) ||
      hasPositiveBalls(innings?.balls) ||
      hasPositiveBalls(innings?.totalBalls) ||
      parseOversToBalls(innings?.overs) > 0
    );

    if (inningsHasBalls) {
      return true;
    }
  }

  if (parseOversToBalls(match?.score?.oversBowled) > 0 || parseOversToBalls(match?.summary?.overs) > 0) {
    return true;
  }

  if (hasDeliveryEvents(match?.timeline) || hasDeliveryEvents(match?.events)) {
    return true;
  }

  return false;
}

export const isFirstBallBowled = hasFirstBallBowled;

const normalizeTeamName = (value) => (typeof value === 'string' ? value.trim() : '');

const normalizeMatchId = (match) => normalizeId(match?.id || match?._id || match?.matchId);

export function isTossCompleted(match) {
  if (!match || typeof match !== 'object') {
    return false;
  }

  const team1 = normalizeTeamName(match?.team1);
  const team2 = normalizeTeamName(match?.team2);
  const tossWinner = normalizeTeamName(match?.configuration?.tossWinner);
  const tossDecision = normalizeTeamName(match?.configuration?.tossDecision).toLowerCase();
  const hasValidWinner = Boolean(team1 && team2 && [team1, team2].includes(tossWinner));
  const hasValidDecision = ['bat', 'field'].includes(tossDecision);

  return hasValidWinner && hasValidDecision;
}

export function getMatchStartDestination(match) {
  return isTossCompleted(match) ? 'scoring' : 'toss';
}

export function getStartResumeLabel(match) {
  return hasFirstBallBowled(match) ? 'Resume match' : 'Start match';
}

export function getMatchStartResumePath({ match, matchBasePath, startPath }) {
  const matchId = normalizeMatchId(match);
  const resolvedStartPath = startPath || (matchBasePath && matchId ? `${matchBasePath}/${matchId}/start` : '');

  if (!resolvedStartPath) {
    return '';
  }

  if (getMatchStartDestination(match) !== 'toss') {
    return resolvedStartPath;
  }

  return `${resolvedStartPath}?view=toss`;
}
